import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Package, Calendar, GitBranch, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import { 
  getCurrentVersionInfo, 
  getAllVersions, 
  getVersionInfo, 
  formatVersion,
  isLatestVersion 
} from '../lib/version'
import { cn } from '../lib/utils'

export function VersionDisplay() {
  const [isExpanded, setIsExpanded] = useState(false)
  const currentVersion = getCurrentVersionInfo()
  const allVersions = getAllVersions()

  return (
    <div className="space-y-4">
      {/* Current Version Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="gaming-card"
      >
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="card-title">Current Version</h3>
                <p className="text-sm text-muted-foreground">Application version and build information</p>
              </div>
            </div>
            {isLatestVersion(currentVersion.version) && (
              <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-success/10 border border-success/30">
                <Sparkles className="h-4 w-4 text-success" />
                <span className="text-sm font-medium text-success">Latest</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center space-x-3 p-3 glass rounded-xl border border-border/30">
              <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                <Package className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Version</p>
                <p className="text-lg font-bold text-foreground">{formatVersion(currentVersion.version)}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 glass rounded-xl border border-border/30">
              <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Build Date</p>
                <p className="text-lg font-bold text-foreground">{currentVersion.buildDate}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 glass rounded-xl border border-border/30">
              <div className="p-2 rounded-lg bg-muted/50 border border-border/30">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Build Number</p>
                <p className="text-lg font-bold text-foreground">#{currentVersion.buildNumber}</p>
              </div>
            </div>
          </div>

          {/* Changelog */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-foreground">What's New</h4>
              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/20 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-sm font-medium">
                  {isExpanded ? 'Show Less' : 'Show More'}
                </span>
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </motion.div>
              </motion.button>
            </div>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {/* Features */}
                  <div>
                    <h5 className="font-semibold text-success mb-2">✨ New Features</h5>
                    <ul className="space-y-2">
                      {currentVersion.features.map((feature, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start space-x-2 text-sm"
                        >
                          <span className="w-1.5 h-1.5 bg-success rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-foreground">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Fixes */}
                  <div>
                    <h5 className="font-semibold text-info mb-2">🔧 Fixes & Improvements</h5>
                    <ul className="space-y-2">
                      {currentVersion.fixes.map((fix, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (index + currentVersion.features.length) * 0.1 }}
                          className="flex items-start space-x-2 text-sm"
                        >
                          <span className="w-1.5 h-1.5 bg-info rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-foreground">{fix}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Version History */}
                  <div>
                    <h5 className="font-semibold text-purple mb-2">📋 Version History</h5>
                    <div className="space-y-2">
                      {allVersions.map((version, index) => {
                        const versionInfo = getVersionInfo(version)
                        if (!versionInfo) return null
                        
                        return (
                          <motion.div
                            key={version}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={cn(
                              "p-3 rounded-lg border transition-colors",
                              version === currentVersion.version
                                ? "bg-primary/10 border-primary/30"
                                : "bg-muted/20 border-border/30"
                            )}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-foreground">
                                {formatVersion(version)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {versionInfo.buildDate}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {versionInfo.changelog[0]}
                            </p>
                          </motion.div>
                        )
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quick Changelog Preview */}
            {!isExpanded && (
              <div className="space-y-2">
                {currentVersion.changelog.slice(0, 3).map((change, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-2 text-sm"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-foreground">{change}</span>
                  </motion.div>
                ))}
                {currentVersion.changelog.length > 3 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    +{currentVersion.changelog.length - 3} more changes
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
} 